'use client'

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'

import './hllbnt-mark.css'

const SCRAMBLE_CHARS = '.,:;~+=*#%@$&!?/\\|<>[]{}()^`\'"0123456789'
const BLOCK_CHARS = '█▓▒░'

const PINK_SHADES = [
  '#FF00AF',
  '#ff00ff',
  '#FF1493',
  '#FF69B4',
  '#FF00D7',
  '#E000C0',
  '#FF33CC',
  '#CC00AA',
  '#FF0080',
  '#D600D6',
] as const

function randomChar() {
  return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]
}

function randomBlockChar() {
  return BLOCK_CHARS[Math.floor(Math.random() * BLOCK_CHARS.length)]
}

function randomPinkShade() {
  return PINK_SHADES[Math.floor(Math.random() * PINK_SHADES.length)]
}

type Phase = 'idle' | 'scramble' | 'resolved' | 'glitch'

interface Snapshot {
  chars: string[]
  phase: Phase
  glitchMask: boolean[]
  colors: string[]
}

function createStore(text: string) {
  const TEXT_CHARS = Array.from(text)
  const initial: Snapshot = {
    chars: [...TEXT_CHARS],
    phase: 'idle',
    glitchMask: new Array(TEXT_CHARS.length).fill(false),
    colors: TEXT_CHARS.map((_, i) => PINK_SHADES[i % PINK_SHADES.length]),
  }
  let snapshot = initial
  const listeners = new Set<() => void>()
  return {
    chars: TEXT_CHARS,
    subscribe(fn: () => void) {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    getSnapshot: () => snapshot,
    update(partial: Partial<Snapshot>) {
      snapshot = { ...snapshot, ...partial }
      for (const fn of listeners) fn()
    },
  }
}

type Store = ReturnType<typeof createStore>

function nonSpaceIndex(chars: string[]): number {
  let i: number
  let safety = 0
  do {
    i = Math.floor(Math.random() * chars.length)
    if (safety++ > 24) break
  } while (chars[i] === ' ')
  return i
}

const GLITCH_EFFECTS: Array<(store: Store, timeouts: ReturnType<typeof setTimeout>[]) => void> = [
  // Single character swap and snap back
  (store, timeouts) => {
    const i = nonSpaceIndex(store.chars)
    if (store.chars[i] === ' ') return
    const next = [...store.chars]
    next[i] = randomChar()
    store.update({ chars: next })
    timeouts.push(setTimeout(() => store.update({ chars: [...store.chars] }), 150))
  },

  // Two characters swap simultaneously
  (store, timeouts) => {
    if (store.chars.length < 2) return
    const next = [...store.chars]
    const a = nonSpaceIndex(store.chars)
    let b = nonSpaceIndex(store.chars)
    let safety = 0
    while ((b === a || store.chars[b] === ' ') && safety++ < 6) b = nonSpaceIndex(store.chars)
    if (store.chars[a] === ' ' || store.chars[b] === ' ' || a === b) return
    next[a] = randomChar()
    next[b] = randomChar()
    store.update({ chars: next })
    timeouts.push(setTimeout(() => store.update({ chars: [...store.chars] }), 120))
  },

  // Block character burst
  (store, timeouts) => {
    const next = [...store.chars]
    const mask = new Array(store.chars.length).fill(false)
    const target = Math.min(2 + Math.floor(Math.random() * 2), store.chars.length)
    const indices = new Set<number>()
    let safety = 0
    while (indices.size < target && safety++ < 24) {
      const i = nonSpaceIndex(store.chars)
      if (store.chars[i] !== ' ') indices.add(i)
    }
    for (const i of indices) {
      next[i] = randomBlockChar()
      mask[i] = true
    }
    store.update({ chars: next, phase: 'glitch', glitchMask: mask })
    timeouts.push(
      setTimeout(() => {
        store.update({
          chars: [...store.chars],
          phase: 'idle',
          glitchMask: new Array(store.chars.length).fill(false),
        })
      }, 100)
    )
  },

  // Rapid triple flicker
  (store, timeouts) => {
    const flicker = (step: number) => {
      if (step >= 3) {
        store.update({
          chars: [...store.chars],
          phase: 'idle',
          glitchMask: new Array(store.chars.length).fill(false),
        })
        return
      }
      store.update({
        chars: store.chars.map((c) => (c === ' ' ? ' ' : randomChar())),
        phase: 'glitch',
        glitchMask: store.chars.map((c) => c !== ' '),
      })
      timeouts.push(
        setTimeout(() => {
          store.update({ chars: [...store.chars], phase: 'idle' })
          timeouts.push(setTimeout(() => flicker(step + 1), 60))
        }, 40)
      )
    }
    flicker(0)
  },

  // Wave: characters flip left-to-right
  (store, timeouts) => {
    store.chars.forEach((char, i) => {
      if (char === ' ') return
      timeouts.push(
        setTimeout(() => {
          const chars = [...store.getSnapshot().chars]
          chars[i] = randomChar()
          store.update({ chars })
        }, i * 40)
      )
      timeouts.push(
        setTimeout(
          () => {
            const chars = [...store.getSnapshot().chars]
            chars[i] = char
            store.update({ chars })
          },
          i * 40 + 80
        )
      )
    })
  },
]

export interface HllbntMarkHandle {
  startScramble: () => void
  startIdle: () => void
  fireGlitch: () => void
}

interface HllbntMarkProps {
  /** Text to render. Spaces are preserved, never scrambled. */
  text: string
  /**
   * When true (default), the mark handles its own hover/focus/click events.
   * Set false when a parent (e.g. an `<a>` wrapper) owns the interactivity
   * and calls `startScramble`/`startIdle`/`fireGlitch` via ref instead.
   */
  interactive?: boolean
  /** Optional aria-label override; defaults to `text`. */
  ariaLabel?: string
  /**
   * Layout variant. `mark` (default) is the brand mark: uppercase, 3px
   * tracking, fixed font, every char a centred 0.65em box. `block` is the
   * raw-ASCII mode. Inherits font and size, preserves multi-space runs via
   * white-space: pre, and lets each glyph sit on its natural mono width.
   */
  variant?: 'mark' | 'block'
}

/**
 * Animated HLLBNT brand mark. Per-character pink colour cycling, five idle
 * glitch effects fired at random, scramble→resolve on hover/focus, and a
 * subtle scanline + phosphor glow on hover.
 *
 * Used inside the public-site footer attribution and the admin chrome so
 * both surfaces are visually identical.
 */
export const HllbntMark = forwardRef<HllbntMarkHandle, HllbntMarkProps>(function HllbntMark(
  { text, interactive = true, ariaLabel, variant = 'mark' },
  ref
) {
  const store = useMemo(() => createStore(text), [text])
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const isHoveredRef = useRef(false)
  const isVisibleRef = useRef(false)
  const elementRef = useRef<HTMLSpanElement | null>(null)

  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    for (const t of timeoutsRef.current) clearTimeout(t)
    timeoutsRef.current = []
  }, [])

  const fireGlitch = useCallback(() => {
    const effect = GLITCH_EFFECTS[Math.floor(Math.random() * GLITCH_EFFECTS.length)]
    effect(store, timeoutsRef.current)
  }, [store])

  const startIdle = useCallback(() => {
    clearTimers()
    isHoveredRef.current = false
    store.update({
      chars: [...store.chars],
      phase: 'idle',
      glitchMask: new Array(store.chars.length).fill(false),
    })

    const scheduleNext = () => {
      const delay = 2000 + Math.random() * 3000
      intervalRef.current = setTimeout(() => {
        if (!isHoveredRef.current && isVisibleRef.current) fireGlitch()
        scheduleNext()
      }, delay) as unknown as ReturnType<typeof setInterval>
    }
    scheduleNext()
  }, [clearTimers, store, fireGlitch])

  const startScramble = useCallback(() => {
    clearTimers()
    isHoveredRef.current = true
    store.update({ phase: 'scramble', glitchMask: new Array(store.chars.length).fill(false) })

    const scramble = setInterval(() => {
      store.update({ chars: store.chars.map((c) => (c === ' ' ? ' ' : randomChar())) })
    }, 50)
    intervalRef.current = scramble

    const resolveStart = setTimeout(() => {
      clearInterval(scramble)
      intervalRef.current = null

      const locked = new Array(store.chars.length).fill(false)
      store.chars.forEach((char, i) => {
        const t = setTimeout(() => {
          locked[i] = true
          const current = [...store.getSnapshot().chars]
          current[i] = char
          store.update({ chars: current })
          if (locked.every(Boolean)) store.update({ phase: 'resolved' })
        }, i * 80)
        timeoutsRef.current.push(t)
      })

      const resolveScramble = setInterval(() => {
        const current = [...store.getSnapshot().chars]
        for (let i = 0; i < store.chars.length; i++) {
          if (!locked[i] && store.chars[i] !== ' ') current[i] = randomChar()
        }
        store.update({ chars: current })
      }, 50)
      intervalRef.current = resolveScramble

      timeoutsRef.current.push(
        setTimeout(
          () => {
            clearInterval(resolveScramble)
            intervalRef.current = null
          },
          store.chars.length * 80 + 50
        )
      )
    }, 400)
    timeoutsRef.current.push(resolveStart)
  }, [clearTimers, store])

  useImperativeHandle(ref, () => ({ startScramble, startIdle, fireGlitch }), [
    startScramble,
    startIdle,
    fireGlitch,
  ])

  // Per-char colour cycling: swap 1-2 random non-space chars every 300ms
  useEffect(() => {
    const colorCycle = setInterval(() => {
      const next = [...store.getSnapshot().colors]
      const count = 1 + Math.floor(Math.random() * 2)
      for (let c = 0; c < count; c++) {
        const i = nonSpaceIndex(store.chars)
        if (store.chars[i] !== ' ') next[i] = randomPinkShade()
      }
      store.update({ colors: next })
    }, 300)
    return () => clearInterval(colorCycle)
  }, [store])

  // IntersectionObserver pause when off-screen
  useEffect(() => {
    const el = elementRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    startIdle()
    return clearTimers
  }, [startIdle, clearTimers])

  const { chars, phase, glitchMask, colors } = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  )

  const ownHandlers = interactive
    ? {
        onMouseEnter: startScramble,
        onMouseLeave: startIdle,
        onFocus: startScramble,
        onBlur: startIdle,
        onClick: fireGlitch,
        tabIndex: 0,
      }
    : {}

  return (
    <span
      ref={elementRef}
      className={[
        'hllbnt-mark',
        variant === 'block' ? 'hllbnt-mark--block' : null,
        interactive ? 'hllbnt-mark--interactive' : null,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={ariaLabel ?? text}
      {...ownHandlers}
    >
      {chars.map((char, i) => {
        const charClass = [
          'hllbnt-mark__char',
          phase === 'scramble' ? 'hllbnt-mark__char--scrambling' : null,
        ]
          .filter(Boolean)
          .join(' ')
        const charColor = glitchMask[i] ? '#ff00ff' : colors[i]
        return (
          <span
            key={i}
            className={charClass}
            style={
              char === ' ' ? undefined : ({ '--char-color': charColor } as React.CSSProperties)
            }
          >
            {char === ' ' ? ' ' : char}
          </span>
        )
      })}
    </span>
  )
})
